
import { useState } from 'react';
import { FileBarChart, Lightbulb, ShieldCheck, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dashboard } from '@/components/dashboard/Dashboard';
import { AIReportModal } from '@/components/reports/AIReportModal';
import { AdSuggestionsModal } from '@/components/ad-suggestions/AdSuggestionsModal';
import { RestrictedFeature } from '@/components/ui/RestrictedFeature';
import { useAIReport } from '@/hooks/useAIReport';
import { useAdSuggestions } from '@/hooks/useAdSuggestions';
import { useAdminClinicaPersistente } from '@/hooks/useAdminClinicaPersistente';
import { usePlanPermissions } from '@/hooks/usePlanPermissions';
import { AdminClinicSelector } from '@/components/admin/AdminClinicSelector';
import { ThinkingOrbLoader } from '@/components/ui/ThinkingOrbLoader';

/**
 * Página do Dashboard
 * 
 * Exibe métricas e indicadores importantes da clínica.
 * Para administradores, permite selecionar qual clínica visualizar com persistência entre sessões.
 * Para usuários normais, mostra apenas dados da própria clínica.
 * 
 * FUNCIONALIDADES:
 * - Botão para gerar relatórios de análise de IA (COM RESTRIÇÕES DE PLANO)
 * - NOVO: Botão para gerar sugestões de anúncios (COM RESTRIÇÕES DE PLANO)
 * - Modal para configuração e criação de relatórios e sugestões
 * - Seletor de clínica para administradores com persistência
 * - Possibilidade de cancelar relatórios em andamento
 * - Controle de limite mensal de relatórios e sugestões
 */
const DashboardPage = () => {
  const {
    clinicaSelecionada,
    selecionarClinica,
    isLoading: adminLoading,
    isAdmin
  } = useAdminClinicaPersistente();

  // Hook para verificar permissões do plano
  const {
    canGenerateReports,
    isPMKPlan,
    hasAIOverride,
    planName,
    monthlyReportLimit,
    isLoading: permissionsLoading
  } = usePlanPermissions();

  // Hook do relatório IA - agora usa a clínica persistida
  const targetClinicaId = isAdmin ? clinicaSelecionada?.id : undefined;

  const {
    isModalOpen,
    openModal,
    closeModal,
    createReport,
    cancelReport,
    isCreatingReport,
    isCancellingReport,
    pendingReports,
    currentProcessingReport,
    completedReports,
    createReportV2,
    isCreatingReportV2
  } = useAIReport(targetClinicaId);

  const [isV2ModalOpen, setIsV2ModalOpen] = useState(false);

  // NOVO: Hook para sugestões de anúncios
  const {
    isModalOpen: isSuggestionsModalOpen,
    openModal: openSuggestionsModal,
    closeModal: closeSuggestionsModal,
    createSuggestions,
    isCreatingSuggestions,
    pendingSuggestions,
    completedSuggestions
  } = useAdSuggestions(targetClinicaId);

  // Mostrar loading enquanto verifica privilégios admin e permissões
  if (adminLoading || permissionsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <ThinkingOrbLoader state="working" size={64} label="Carregando dashboard..." />
      </div>
    );
  }

  // Calcular quantos relatórios foram usados este mês
  const currentMonth = new Date().getMonth();
  const currentYear = new Date().getFullYear();
  const reportsThisMonth = completedReports?.filter(report => {
    const reportDate = new Date(report.created_at);
    return reportDate.getMonth() === currentMonth && reportDate.getFullYear() === currentYear;
  }).length || 0;

  // NOVO: Calcular quantas sugestões foram usadas este mês
  const suggestionsThisMonth = completedSuggestions?.filter(suggestion => {
    const suggestionDate = new Date(suggestion.created_at);
    return suggestionDate.getMonth() === currentMonth && suggestionDate.getFullYear() === currentYear;
  }).length || 0;

  // Verificar se pode gerar novo relatório baseado no limite
  const canGenerateNewReport = () => {
    if (isAdmin) return true;
    if (!canGenerateReports) return false;
    if (monthlyReportLimit === 999) return true;
    return reportsThisMonth < monthlyReportLimit;
  };

  // NOVO: Verificar se pode gerar nova sugestão baseado no limite (mesmo limite dos relatórios)
  const canGenerateNewSuggestion = () => {
    if (isAdmin) return true;
    if (!canGenerateReports) return false; // Usar mesma permissão dos relatórios
    if (monthlyReportLimit === 999) return true;
    return suggestionsThisMonth < monthlyReportLimit;
  };

  // Função para renderizar botão de relatório ou estado restrito
  const renderReportButton = () => {
    // Se não pode gerar relatórios pelo plano
    if (!canGenerateReports && !isAdmin) {
      return (
        <RestrictedFeature
          planName={planName}
          featureName="Relatórios de IA"
          featureDescription="Gere relatórios detalhados com análise de IA sobre o desempenho da sua clínica."
          isAdmin={isAdmin}
          hasOverride={hasAIOverride}
          variant="inline"
          showUpgrade={!isPMKPlan}
          hideForPMK={isPMKPlan}
        />
      );
    }

    // Se atingiu o limite mensal
    if (!canGenerateNewReport() && !isAdmin) {
      return (
        <div className="flex w-full items-center gap-2 rounded-full bg-warning-subtle px-4 py-2 text-xs font-semibold text-warning-text sm:w-auto sm:text-sm">
          <FileBarChart className="w-4 h-4" />
          <span>
            Limite mensal atingido ({reportsThisMonth}/{monthlyReportLimit})
          </span>
        </div>
      );
    }

    return (
      <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 w-full sm:w-auto">
        {!isAdmin && monthlyReportLimit < 999 && (
          <div className="text-xs text-muted-foreground sm:whitespace-nowrap">
            {reportsThisMonth}/{monthlyReportLimit} relatórios este mês
          </div>
        )}

        {/* Ação principal do cabeçalho: botão lima com texto tinta (design system 9.1), uma por área. */}
        <Button
          onClick={() => setIsV2ModalOpen(true)}
          className="h-10 w-full justify-center rounded-full px-5 font-semibold shadow-none transition-[background-color,transform] duration-150 hover:bg-brand-soft active:scale-[0.98] sm:w-auto"
          disabled={isCreatingReportV2 || isCancellingReport}
        >
          <Sparkles className="h-4 w-4" aria-hidden="true" />
          Gerar Relatório IA
        </Button>
      </div>
    );
  };

  // NOVO: Função para renderizar botão de sugestões ou estado restrito
  const renderSuggestionsButton = () => {
    // Se não pode gerar sugestões pelo plano
    if (!canGenerateReports && !isAdmin) {
      return (
        <RestrictedFeature
          planName={planName}
          featureName="Sugestões de Anúncios"
          featureDescription="Receba sugestões de anúncios geradas por IA para melhorar suas campanhas."
          isAdmin={isAdmin}
          hasOverride={hasAIOverride}
          variant="inline"
          showUpgrade={!isPMKPlan}
          hideForPMK={isPMKPlan}
        />
      );
    }

    // Se atingiu o limite mensal
    if (!canGenerateNewSuggestion() && !isAdmin) {
      return (
        <div className="flex w-full items-center gap-2 rounded-full bg-warning-subtle px-4 py-2 text-xs font-semibold text-warning-text sm:w-auto sm:text-sm">
          <Lightbulb className="w-4 h-4" />
          <span>
            Limite mensal atingido ({suggestionsThisMonth}/{monthlyReportLimit})
          </span>
        </div>
      );
    }

    return (
      <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 w-full sm:w-auto">
        {!isAdmin && monthlyReportLimit < 999 && (
          <div className="text-xs text-muted-foreground sm:whitespace-nowrap">
            {suggestionsThisMonth}/{monthlyReportLimit} sugestões este mês
          </div>
        )}

        {/* Ação secundária: contorno branco com borda suave e hover lima névoa (variante outline do design system). */}
        <Button
          onClick={openSuggestionsModal}
          variant="outline"
          className="h-10 w-full justify-center rounded-full border-input bg-card px-5 font-semibold text-foreground transition-[background-color,color,transform] duration-150 hover:bg-accent hover:text-accent-foreground active:scale-[0.98] sm:w-auto"
          disabled={isCreatingSuggestions}
        >
          <Lightbulb className="h-4 w-4 text-accent-foreground" aria-hidden="true" />
          Gerar Sugestões de Anúncios
        </Button>
      </div>
    );
  };

  // Agrupa os botões do topo para o DashboardHeader manter título, ações e exportação na mesma linha visual.
  const dashboardHeaderActions = (!isAdmin || clinicaSelecionada) ? (
    <>
      {/* Mostra processamento em andamento antes das ações para o usuário entender por que algum botão pode estar desabilitado. */}
      {(pendingReports.length > 0 || pendingSuggestions.length > 0) && (
        <div className="flex h-10 items-center gap-2 rounded-full bg-warning-subtle px-4 text-xs font-semibold text-warning-text sm:text-sm" role="status">
          <div className="h-4 w-4 animate-spin rounded-full border-2 border-warning/30 border-t-warning motion-reduce:animate-none"></div>
          <span>
            {pendingReports.length + pendingSuggestions.length} processo{pendingReports.length + pendingSuggestions.length > 1 ? 's' : ''} ativo{pendingReports.length + pendingSuggestions.length > 1 ? 's' : ''}
          </span>
        </div>
      )}
      {renderSuggestionsButton()}
      {renderReportButton()}
    </>
  ) : null;

  return (
    <div className="space-y-6">
      {/* Seletor de clínica para administradores com persistência */}
      {isAdmin && (
        // Mesmo painel do Funil e de Vendas (design system): cartão branco com borda musgo, sem degradê esmeralda.
        <section aria-label="Modo Administrador" className="relative rounded-[24px] border border-border bg-card p-5 shadow-[0_1px_2px_rgba(19,41,27,0.05),0_3px_10px_rgba(19,41,27,0.05)]">
          <div className="relative mb-4 flex flex-wrap items-start justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="flex h-8 w-8 items-center justify-center rounded-full border border-border bg-muted">
                <ShieldCheck className="h-5 w-5 text-accent-foreground" aria-hidden="true" />
              </div>
              <div>
                <p className="text-sm font-semibold text-foreground">Modo Administrador</p>
                <p className="text-xs text-muted-foreground">Selecione uma clínica para visualizar</p>
              </div>
            </div>
            {clinicaSelecionada && (
              <div className="flex items-center gap-2 rounded-full bg-muted px-3 py-1.5">
                <div className="h-2 w-2 animate-pulse rounded-full bg-success motion-reduce:animate-none"></div>
                <span className="text-xs font-medium text-foreground">
                  Ativa: {clinicaSelecionada.nome}
                </span>
              </div>
            )}
          </div>
          <div className="relative">
            <AdminClinicSelector
              clinicaSelecionada={clinicaSelecionada}
              onClinicaSelected={selecionarClinica}
              showStats={true}
            />
          </div>
        </section>
      )}

      {/* Componente Dashboard */}
      {(!isAdmin || clinicaSelecionada) ? (
        <Dashboard
          adminMode={isAdmin}
          targetClinicaId={isAdmin ? clinicaSelecionada?.id : undefined}
          clinicName={isAdmin ? clinicaSelecionada?.nome : null}
          headerActions={dashboardHeaderActions}
        />
      ) : (
        <div className="flex h-64 items-center justify-center rounded-[24px] border-2 border-dashed border-input bg-card">
          <div className="text-center">
            <FileBarChart className="mx-auto mb-4 h-12 w-12 text-muted-foreground" aria-hidden="true" />
            <h3 className="mb-2 text-lg font-medium text-foreground">Selecione uma clínica</h3>
            <p className="text-muted-foreground">
              Escolha uma clínica no seletor acima para visualizar seu dashboard
            </p>
          </div>
        </div>
      )}

      {/* Modal para criação de relatório */}
      <AIReportModal
        isOpen={isModalOpen}
        onClose={closeModal}
        onCreateReport={createReport}
        onCancelReport={cancelReport}
        isCreating={isCreatingReport}
        isCancelling={isCancellingReport}
        currentProcessingReport={currentProcessingReport}
        adminMode={isAdmin}
        selectedClinica={clinicaSelecionada}
        monthlyReportLimit={monthlyReportLimit}
        reportsThisMonth={reportsThisMonth}
      />

      {/* NOVO: Modal para criação de relatório V2 Nativo */}
      <AIReportModal
        isNewFlow={true}
        isOpen={isV2ModalOpen}
        onClose={() => setIsV2ModalOpen(false)}
        onCreateReport={createReportV2}
        onCancelReport={cancelReport}
        isCreating={isCreatingReportV2}
        isCancelling={isCancellingReport}
        currentProcessingReport={currentProcessingReport}
        adminMode={isAdmin}
        selectedClinica={clinicaSelecionada}
        monthlyReportLimit={monthlyReportLimit}
        reportsThisMonth={reportsThisMonth}
      />

      {/* NOVO: Modal para criação de sugestões de anúncios */}
      <AdSuggestionsModal
        isOpen={isSuggestionsModalOpen}
        onClose={closeSuggestionsModal}
        onCreateSuggestions={createSuggestions}
        isCreating={isCreatingSuggestions}
        adminMode={isAdmin}
        selectedClinica={clinicaSelecionada}
        monthlyLimit={monthlyReportLimit}
        suggestionsThisMonth={suggestionsThisMonth}
      />
    </div>
  );
};

export default DashboardPage;
